import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import TemplateBuilder from '../components/TemplateBuilder/TemplateBuilder';
import type { EnhancedTemplate } from '../types/templateTypes';
import {
  Plus,
  RefreshCw,
  Edit,
  Eye,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EnhancedTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EnhancedTemplate | null>(null);
  const [activeView, setActiveView] = useState<'list' | 'builder'>('list');
  const [filterMultiSheet, setFilterMultiSheet] = useState<'all' | 'single' | 'multi'>('all');

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const getTemplates = httpsCallable(functions, 'getTemplates');
      const result = await getTemplates({}) as any;

      console.log('[TemplatesPage] getTemplates result:', JSON.stringify(result.data, null, 2));

      if (result.data.success) {
        console.log('[TemplatesPage] Setting templates:', result.data.templates.length, 'templates');
        setTemplates(result.data.templates);
      }
    } catch (error) {
      console.error('[TemplatesPage] Error loading templates:', error);
      console.error('[TemplatesPage] Using mock data due to error');
      // Use mock data for now since backend isn't connected
      setTemplates([
        {
          id: 'template_1',
          name: 'LMG Monthly Commission',
          aggregator: 'LMG',
          fileType: 'xlsx',
          status: 'active',
          parserConfig: { headerRow: 0 },
          columnMappings: {},
          paymentTypeConfig: { columnName: 'Payment Type', mappingStrategy: 'exact' },
          runHandling: { strategy: 'sequential' },
          validation: { requiredColumns: [], dataQualityChecks: [] },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1
        },
        {
          id: 'template_2',
          name: 'AFG Commission Statement',
          aggregator: 'AFG',
          fileType: 'csv',
          status: 'draft',
          parserConfig: { headerRow: 0 },
          columnMappings: {},
          paymentTypeConfig: { columnName: 'COMMISSION_TYPE', mappingStrategy: 'fuzzy' },
          runHandling: { strategy: 'sequential' },
          validation: { requiredColumns: [], dataQualityChecks: [] },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (template: EnhancedTemplate) => {
    try {
      const saveTemplate = httpsCallable(functions, 'saveTemplate');
      const result = await saveTemplate(template) as any;  // ✅ Pass template directly, not wrapped

      if (result.data.success) {
        console.log('Template saved successfully');
        setActiveView('list');
        setSelectedTemplate(null);
        await loadTemplates();  // Wait for templates to refresh
      }
    } catch (error: any) {
      console.error('Error saving template:', error);
      // Show error to user instead of silently failing
      const errorMessage = error?.message || 'Failed to save template. Please try again.';
      alert(`Error: ${errorMessage}`);
      // Don't switch views on error - let user fix the issue
    }
  };

  const handleEditTemplate = (template: EnhancedTemplate) => {
    setSelectedTemplate(template);
    setActiveView('builder');
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setActiveView('builder');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'draft':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'deprecated':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      deprecated: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${styles[status as keyof typeof styles] || styles.draft}`}>
        {getStatusIcon(status)}
        <span className="text-xs font-medium uppercase">{status}</span>
      </span>
    );
  };

  if (activeView === 'builder') {
    return (
      <div className="templates-page">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {selectedTemplate ? 'Edit Template' : 'New Template'}
            </h1>
            <p className="text-slate-400">
              Configure file processing for aggregator commission files
            </p>
          </div>
          <button
            onClick={() => {
              setActiveView('list');
              setSelectedTemplate(null);
            }}
            className="px-4 py-2 bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
          >
            Back to List
          </button>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
          <TemplateBuilder
            existingTemplate={selectedTemplate || undefined}
            onSave={handleSaveTemplate}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="templates-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-8 mb-8 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileSpreadsheet className="w-8 h-8 text-white/90" />
              <h1 className="text-3xl font-bold text-white">File Templates</h1>
            </div>
            <p className="text-emerald-100">
              Manage file templates for different aggregators and their payment type mappings
            </p>
            <div className="flex gap-6 mt-4">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-200" />
                <span className="text-emerald-100">Total Templates</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-200" />
                <span className="text-emerald-100">Active Templates</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadTemplates}
              disabled={loading}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleNewTemplate}
              className="px-4 py-2 bg-white text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />
              New Template
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-6 gap-6 mb-8">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
            <span className="text-xs font-medium text-slate-400 uppercase">TOTAL</span>
          </div>
          <div className="text-2xl font-bold text-white">{templates.length}</div>
          <div className="text-sm text-slate-400">Templates</div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-xs font-medium text-slate-400 uppercase">ACTIVE</span>
          </div>
          <div className="text-2xl font-bold text-white">
            {templates.filter(t => t.status === 'active').length}
          </div>
          <div className="text-sm text-slate-400">Templates</div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            <span className="text-xs font-medium text-slate-400 uppercase">DRAFT</span>
          </div>
          <div className="text-2xl font-bold text-white">
            {templates.filter(t => t.status === 'draft').length}
          </div>
          <div className="text-sm text-slate-400">Templates</div>
        </div>

        <div
          className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 cursor-pointer hover:border-purple-500/50 transition-colors"
          onClick={() => setFilterMultiSheet(filterMultiSheet === 'multi' ? 'all' : 'multi')}
        >
          <div className="flex items-center justify-between mb-2">
            <FileSpreadsheet className="w-5 h-5 text-purple-400" />
            <span className="text-xs font-medium text-slate-400 uppercase">MULTI-SHEET</span>
          </div>
          <div className="text-2xl font-bold text-white">
            {templates.filter(t => (t as any).isMultiSheet === true).length}
          </div>
          <div className="text-sm text-purple-400">
            {filterMultiSheet === 'multi' ? 'Filtered' : 'Click to filter'}
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-orange-500/30">
          <div className="flex items-center justify-between mb-2">
            <FileSpreadsheet className="w-5 h-5 text-orange-400" />
            <span className="text-xs font-medium text-slate-400 uppercase">ACQUIRED</span>
          </div>
          <div className="text-2xl font-bold text-white">
            {templates.filter(t => (t as any).isAcquiredLoanBook === true).length}
          </div>
          <div className="text-sm text-orange-400">Loan Books</div>
        </div>

      </div>

      {/* Templates Table */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
        <div className="px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Template Registry</h2>

            {/* Filter Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilterMultiSheet('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterMultiSheet === 'all'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                }`}
              >
                All Templates
              </button>
              <button
                onClick={() => setFilterMultiSheet('single')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterMultiSheet === 'single'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Single-Sheet
              </button>
              <button
                onClick={() => setFilterMultiSheet('multi')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterMultiSheet === 'multi'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Multi-Sheet
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-slate-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="p-12 text-center">
            <FileSpreadsheet className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No templates found</p>
            <button
              onClick={handleNewTemplate}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create First Template
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-xs font-medium text-slate-400 uppercase bg-slate-900/50">
                <tr>
                  <th className="px-6 py-4 text-left">Template</th>
                  <th className="px-6 py-4 text-left">Aggregator</th>
                  <th className="px-6 py-4 text-left">File Type</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Sheets/Mappings</th>
                  <th className="px-6 py-4 text-left">Updated</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {templates
                  .filter(t => {
                    if (filterMultiSheet === 'all') return true;
                    if (filterMultiSheet === 'single') return !(t as any).isMultiSheet;
                    if (filterMultiSheet === 'multi') return (t as any).isMultiSheet === true;
                    return true;
                  })
                  .map((template) => (
                  <tr key={template.id} className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{template.name}</span>
                            {(template as any).isMultiSheet && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium">
                                Multi-Sheet
                              </span>
                            )}
                            {(template as any).isAcquiredLoanBook && (
                              <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs font-medium border border-orange-500/30">
                                Acquired
                              </span>
                            )}
                          </div>
                          {(template as any).acquisitionDate && (
                            <div className="text-xs text-slate-500 mt-1">
                              Acquired: {new Date((template as any).acquisitionDate).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm">
                        {template.aggregator}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm uppercase">
                        {template.fileType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(template.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-300">
                        {(template as any).isMultiSheet ? (
                          <div className="group relative">
                            <div className="flex items-center gap-2 cursor-help">
                              <FileSpreadsheet className="w-4 h-4 text-purple-400" />
                              <span className="font-medium">
                                {Object.keys((template as any).worksheetMapping || {}).length} worksheets
                              </span>
                            </div>
                            {/* Tooltip with worksheet details */}
                            <div className="absolute left-0 top-full mt-2 hidden group-hover:block z-10 bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl min-w-[200px]">
                              <div className="text-xs space-y-1">
                                {(template as any).worksheetMapping?.upfront && (
                                  <div className="text-green-400">Upfront: {(template as any).worksheetMapping.upfront}</div>
                                )}
                                {(template as any).worksheetMapping?.trail && (
                                  <div className="text-blue-400">Trail: {(template as any).worksheetMapping.trail}</div>
                                )}
                                {(template as any).worksheetMapping?.nil && (
                                  <div className="text-yellow-400">Nil: {(template as any).worksheetMapping.nil}</div>
                                )}
                                {(template as any).worksheetMapping?.summary && (
                                  <div className="text-purple-400">Summary: {(template as any).worksheetMapping.summary}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div>{Object.keys(template.columnMappings || {}).length} columns</div>
                            <div className="text-slate-500">
                              {Object.keys(template.paymentTypeConfig?.customMappings || {}).length} payment types
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {new Date(template.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4 text-slate-400" />
                        </button>
                        <button
                          onClick={() => {
                            console.log('View template:', template);
                          }}
                          className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* AFG File Pattern Info */}
      <div className="mt-8 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          AFG File Pattern Detection
        </h3>
        <div className="space-y-3 text-sm">
          <p className="text-slate-300">
            AFG files use statement numbers to identify runs. The pattern is:
          </p>
          <div className="bg-slate-900/50 rounded-lg p-4 font-mono text-xs">
            <div className="text-green-400 mb-2">COMM_STMT_[COMPANY_NAME]-MASTER-[STATEMENT_ID].csv</div>
            <div className="text-slate-400">
              • Statement IDs are sequential (e.g., 1047782, 1051153, 1051484)<br/>
              • Files are dated to indicate run order<br/>
              • PAYMENT_NO column contains the statement ID for verification<br/>
              • Run sequence determined by file date, not ID number
            </div>
          </div>
          <p className="text-slate-400">
            Example August 2025 runs detected:
          </p>
          <ul className="space-y-1 text-slate-400">
            <li>• Run 1: 1047782 (Aug 8) - 3,115 bytes</li>
            <li>• Run 2: 1051153 (Aug 22) - 618 bytes</li>
            <li>• Run 3: 1051484 (Aug 29) - 404 bytes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;